import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useIdea } from '../hooks/api/useIdeas';
import { tokens } from '../design-system';
import { LoadingSpinner } from '../design-system';
import PlanChatbot from '../components/PlanChatbot';
import apiClient from '../services/unifiedApiClient';

/**
 * ChatPage - Fullscreen chat page for pop-out windows.
 *
 * Routes:
 *   /ideas/:ideaId/chat  - idea-scoped chat (canonical)
 *   /plans/:taskId/chat  - redirects to the idea's chat
 */
const ChatPage = () => {
  const { ideaId, taskId } = useParams();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Idea chat mode
  const { data: idea, isLoading: ideaLoading } = useIdea(ideaId, {
    enabled: !!ideaId && isAuthenticated(),
  });

  // Plan chat mode — fetch plan only to get its idea_id for redirect
  const { data: task, isLoading: taskLoading } = useQuery({
    queryKey: ['tasks', 'detail', taskId],
    queryFn: async () => {
      const response = await apiClient.get(`/plans/${taskId}`);
      return response?.data || response;
    },
    enabled: !!taskId && isAuthenticated(),
  });

  // Redirect /plans/:taskId/chat → /ideas/:ideaId/chat
  useEffect(() => {
    if (taskId && task?.idea_id) {
      navigate(`/ideas/${task.idea_id}/chat`, { replace: true });
    }
  }, [taskId, task?.idea_id, navigate]);

  const isLoading = (ideaId && ideaLoading) || (taskId && taskLoading);

  if (isLoading || taskId) {
    return (
      <div style={styles.loading}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PlanChatbot
        ideaId={ideaId}
        task={null}
        taskTree={null}
        idea={idea || null}
        embedded={false}
      />
    </div>
  );
};

const styles = {
  container: {
    height: 'calc(100vh - 60px)',
    backgroundColor: '#F7EFD8',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 60px)',
    backgroundColor: '#F7EFD8',
  },
};

export default ChatPage;
